import React, { useEffect, useState } from 'react';
import QrCodeGenerator, { generateQrDataUrl } from '../shared/QrCodeGenerator';
import PdfDownloadButtons from '../shared/PdfQrSheet';
import Button from '../../ui/Button';

export interface MaterialData {
  nfcInterest: boolean;
}

interface MaterialGenerationStepProps {
  data: MaterialData;
  onUpdate: (data: MaterialData) => void;
  onBack: () => void;
  onComplete: () => void;
  submitting: boolean;
  error: string | null;
  businessName: string;
  ssid: string;
  slug: string;
}

const MaterialGenerationStep: React.FC<MaterialGenerationStepProps> = ({
  data,
  onUpdate,
  onBack,
  onComplete,
  submitting,
  error,
  businessName,
  ssid,
  slug,
}) => {
  const [qrDataUrl, setQrDataUrl] = useState<string>('');
  const venueUrl = `https://whobrew.us/v/${slug || 'preview'}`;

  useEffect(() => {
    generateQrDataUrl(venueUrl, 300)
      .then(setQrDataUrl)
      .catch(() => setQrDataUrl(''));
  }, [venueUrl]);

  return (
    <div className="space-y-6">
      <div>
        <div className="eyebrow mb-1.5">Step 5 of 5</div>
        <h2 className="text-display text-2xl text-coffee-900">Your QR materials</h2>
        <p className="mt-1.5 text-sm text-coffee-600">
          Download print-ready QR code sheets for your tables and walls. You can redownload these from your dashboard any time.
        </p>
      </div>

      {/* QR Code Preview */}
      <div className="flex flex-col items-center p-6 bg-coffee-50 border border-coffee-100 rounded-2xl">
        <QrCodeGenerator url={venueUrl} size={200} />
        <p className="text-sm font-semibold text-coffee-900 mt-3">{businessName}</p>
        <p className="text-xs text-coffee-500 mt-1 select-all font-mono">{venueUrl}</p>
      </div>

      {/* PDF Downloads */}
      {qrDataUrl && (
        <div>
          <div className="eyebrow mb-3">Download print-ready PDFs</div>
          <PdfDownloadButtons
            businessName={businessName}
            ssid={ssid}
            qrDataUrl={qrDataUrl}
            slug={slug || 'preview'}
          />
        </div>
      )}

      {/* NFC Interest */}
      <label className="flex items-start gap-3 p-4 rounded-xl border border-coffee-100 bg-white cursor-pointer hover:border-coffee-200 transition-colors">
        <input
          type="checkbox"
          checked={data.nfcInterest}
          onChange={(e) => onUpdate({ ...data, nfcInterest: e.target.checked })}
          className="mt-0.5 h-4 w-4 rounded border-coffee-300 text-coffee-600 focus:ring-coffee-500"
        />
        <div>
          <span className="text-sm font-medium text-coffee-900">
            Interested in NFC dots for your tables?
          </span>
          <p className="text-xs text-coffee-500 mt-0.5 leading-relaxed">
            NFC dots let guests tap their phone instead of scanning. We'll reach out with pricing and details.
          </p>
        </div>
      </label>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      <div className="flex justify-between pt-2 border-t border-coffee-100">
        <Button variant="ghost" onClick={onBack} disabled={submitting}>
          Back
        </Button>
        <Button
          variant="primary"
          onClick={onComplete}
          disabled={submitting}
          loading={submitting}
        >
          {submitting ? 'Setting up...' : 'Complete Setup'}
        </Button>
      </div>
    </div>
  );
};

export default MaterialGenerationStep;
